import { useEffect, useState } from 'react';
import type { AccessibilityFeature, AccessibilityFeatureRow } from '../api';
import { Button, Field, Panel } from '../ui';
import { INPUT } from '../pages/event/shared';
import { dateWords } from '../terms';

/**
 * What a room or a show offers (D-80).
 *
 * The storefront shows exactly what is ticked here and nothing else — it used
 * to promise hearing assistance and accessible seating on every event, whether
 * or not either was true. A note beside a feature is what the customer reads
 * instead of the standard wording, so "Accessible parking" can say which lot.
 *
 * On an event, **the tick means what the customer sees on this show**, not what
 * this show adds (D-186). A feature the room already offers starts ticked and
 * says so; unticking it is how a venue says "not for this one", which is the
 * thing that could not be said before — a haunt in a room with a hearing loop
 * advertised the loop, and Admin showed an empty box.
 */

export interface AccessibilityDraft {
  notes: string;
  good_to_know_md?: string;
  features: Map<string, string>;
}

export function AccessibilityEditor({
  title,
  description,
  catalog,
  selected,
  notes,
  goodToKnow,
  inherited,
  editable,
  saving,
  onSave,
}: {
  title: string;
  description: string;
  catalog: AccessibilityFeatureRow[];
  selected: AccessibilityFeature[];
  notes: string | null;
  /** Only an event has these; a venue passes undefined. */
  goodToKnow?: string | null;
  /** Features the venue already offers, shown as inherited on an event. */
  inherited?: AccessibilityFeature[];
  editable: boolean;
  saving: boolean;
  onSave: (draft: { notes: string | null; good_to_know_md: string | null; features: { code: string; note: string | null; suppressed?: boolean }[] }) => void;
}) {
  const [features, setFeatures] = useState<Map<string, string>>(new Map());
  /** Venue features this show turns off. Only an event has any (D-186). */
  const [suppressed, setSuppressed] = useState<Set<string>>(new Set());
  const [noteText, setNoteText] = useState('');
  const [goodText, setGoodText] = useState('');
  const [dirty, setDirty] = useState(false);

  const inheritedCodes = new Map((inherited ?? []).map((f) => [f.code, f]));

  useEffect(() => {
    setFeatures(new Map(selected.filter((f) => !f.suppressed).map((f) => [f.code, f.note ?? ''])));
    setSuppressed(new Set(selected.filter((f) => f.suppressed).map((f) => f.code)));
    setNoteText(notes ?? '');
    setGoodText(goodToKnow ?? '');
    setDirty(false);
  }, [selected, notes, goodToKnow]);

  /** What the customer sees: this show's own tick, or the room's, unless turned off. */
  const shown = (code: string) => features.has(code) || (inheritedCodes.has(code) && !suppressed.has(code));

  const toggle = (code: string) => {
    const off = shown(code);
    setFeatures((previous) => {
      const next = new Map(previous);
      if (off) next.delete(code);
      else if (!inheritedCodes.has(code)) next.set(code, '');
      return next;
    });
    // Turning off something the room offers is a decision that has to be
    // written down; turning it back on is just removing that decision.
    setSuppressed((previous) => {
      if (!inheritedCodes.has(code)) return previous;
      const next = new Set(previous);
      if (off) next.add(code);
      else next.delete(code);
      return next;
    });
    setDirty(true);
  };

  return (
    <Panel
      title={title}
      description={description}
      actions={
        editable ? (
          <Button
            variant="primary"
            disabled={!dirty || saving}
            onClick={() =>
              onSave({
                notes: noteText.trim() || null,
                good_to_know_md: goodToKnow === undefined ? null : goodText.trim() || null,
                features: [
                  ...[...features.entries()].map(([code, note]) => ({ code, note: note.trim() || null })),
                  ...[...suppressed].map((code) => ({ code, note: null, suppressed: true })),
                ],
              })
            }
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        ) : null
      }
    >
      <div className="space-y-4 px-5 py-4">
        <ul className="grid gap-2 sm:grid-cols-2">
          {catalog.map((feature) => {
            const fromVenue = inheritedCodes.get(feature.code);
            const on = shown(feature.code);
            const off = Boolean(fromVenue) && suppressed.has(feature.code);
            // A note can only be written on this show's own wording; a feature
            // shown purely because the room offers it reads the room's words.
            const noted = features.has(feature.code);
            return (
              <li key={feature.code} className="rounded-lg border border-line p-3">
                <label className="flex items-start gap-2 text-sm">
                  <input type="checkbox" checked={on} disabled={!editable} onChange={() => toggle(feature.code)} className="mt-1" />
                  <span>
                    <span className="font-medium text-ink-900">{feature.name}</span>
                    {fromVenue ? <span className="ml-2 rounded bg-mist px-1.5 py-0.5 text-xs text-ink-500">at this venue</span> : null}
                    {off ? <span className="ml-2 rounded bg-warn-soft px-1.5 py-0.5 text-xs text-warn">off for this event</span> : null}
                    <span className="block text-xs text-ink-500">{fromVenue?.note?.trim() || feature.description}</span>
                  </span>
                </label>
                {on && fromVenue && !noted ? (
                  <button
                    type="button"
                    disabled={!editable}
                    className="mt-2 text-xs text-ink-500 underline hover:text-ink disabled:opacity-50"
                    onClick={() => {
                      setFeatures((previous) => new Map(previous).set(feature.code, ''));
                      setDirty(true);
                    }}
                  >
                    Say something different about it for this event
                  </button>
                ) : null}
                {noted ? (
                  <input
                    className={`mt-2 w-full ${INPUT}`}
                    placeholder="What to say instead (optional)"
                    value={features.get(feature.code) ?? ''}
                    disabled={!editable}
                    aria-label={`${feature.name} note`}
                    onChange={(e) => {
                      setFeatures((previous) => new Map(previous).set(feature.code, e.target.value));
                      setDirty(true);
                    }}
                  />
                ) : null}
              </li>
            );
          })}
        </ul>
        <Field label="Accessibility notes" htmlFor="access-notes">
          <textarea
            id="access-notes"
            rows={3}
            className={`w-full max-w-3xl ${INPUT}`}
            value={noteText}
            disabled={!editable}
            placeholder="Anything the list above cannot say. Shown under the features."
            onChange={(e) => {
              setNoteText(e.target.value);
              setDirty(true);
            }}
          />
        </Field>
        {goodToKnow === undefined ? null : (
          <Field label="Good to know (one per line)" htmlFor="good-to-know">
            <textarea
              id="good-to-know"
              rows={3}
              className={`w-full max-w-3xl ${INPUT}`}
              value={goodText}
              disabled={!editable}
              placeholder="No bags larger than 12&quot;. Parking is free after 6."
              onChange={(e) => {
                setGoodText(e.target.value);
                setDirty(true);
              }}
            />
            <p className="mt-1 text-xs text-ink-500">{dateWords().gates}, the age restriction and the accessibility highlights are added automatically.</p>
          </Field>
        )}
      </div>
    </Panel>
  );
}
